/**
 * Cedar Insights Tab - Personal Research Timeline
 * 
 * Displays timeline of research-backed decisions:
 * - Recent: Chronological events
 * - Plan: Goals/plans/workouts events (lightning bolt icon)
 * - Cedar: Intelligence conversations (CC chat bubble icon)
 * 
 * This is the MOVED content from the original SourcesAndResources component.
 * 
 * Author: Coach Cedar Development Team
 * Date: December 11, 2025
 * Updated: January 28, 2026 (aligned filter icons with bottom navigation)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResearchEvents, ResearchEventFilter } from '../hooks/useResearchEvents';
import { ResearchEventCard } from './ResearchEventCard';
import { Activity, Zap, Filter, FileText, MessageCircle } from 'lucide-react';
import { ChatWithCedarIcon } from './ChatWithCedarIcon';
import './InsightsV2.css';

interface CedarInsightsTabProps {
  userId: string;
  highlightEventId?: string;  // 🔬 NEW: For deep linking to specific event
  fromChat?: boolean;  // 🔬 NEW: Shows "Return to Chat" button
}

export const CedarInsightsTab: React.FC<CedarInsightsTabProps> = ({ 
  userId, 
  highlightEventId,
  fromChat 
}) => {
  const navigate = useNavigate();
  
  // 🔬 NEW: Track which event drawer should be auto-opened
  const [autoExpandedEventId, setAutoExpandedEventId] = useState<string | undefined>(highlightEventId);
  
  const {
    data,
    loading,
    error,
    filter,
    engine,
    subcategory,
    setFilter,
    setEngine,
    setSubcategory
  } = useResearchEvents({
    initialFilter: 'recent',
    autoLoad: true
  });
  
  // 🔬 AUTO-SCROLL & HIGHLIGHT: When highlightEventId provided
  useEffect(() => {
    if (highlightEventId && data?.events && data.events.length > 0) {
      console.log(`🔬 DEEP LINK: Looking for event ${highlightEventId} to scroll to`);
      
      // Wait for DOM to render, then scroll
      setTimeout(() => {
        const eventElement = document.getElementById(`research-event-${highlightEventId}`);
        if (eventElement) {
          console.log(`✅ DEEP LINK: Found event, scrolling into view`);
          eventElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Add highlight animation
          eventElement.classList.add('research-event-highlight');
          setTimeout(() => {
            eventElement.classList.remove('research-event-highlight');
          }, 3000);  // Remove highlight after 3s
        } else {
          console.log(`⚠️ DEEP LINK: Event ${highlightEventId} not found in DOM`);
        }
      }, 500);  // Give React time to render
    }
  }, [highlightEventId, data]);

  const handleFilterChange = (newFilter: ResearchEventFilter) => {
    setFilter(newFilter);
    setEngine(undefined);
    setSubcategory(undefined);
  };

  const handleChatWithCedar = (eventId: string, event: any) => {
    navigate('/chat', {
      state: {
        from: 'research-event',
        context: {
          eventId,
          eventType: event.event_type,
          eventTitle: event.event_title,
          eventDescription: event.event_description,
          intelligenceEngine: event.intelligence_engine,
          userQuery: event.user_query,
          initialMessage: `Tell me more about this.`,
          goalId: event.goal_id,
          planId: event.plan_id,
          workoutId: event.workout_id
        }
      }
    });
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4">
        <div className="insights-card error-card">
          <div className="insights-card-title">
            <FileText size={18} />
            Error
          </div>
          <div className="insights-card-body">Error loading research events: {error}</div>
        </div>
      </div>
    );
  }

  const events = data?.events || [];
  const totalCount = data?.total_count || 0;

  // Get unique engines from events for filter chips (for Chat filter)
  const availableEngines = filter === 'chat' 
    ? Array.from(new Set(events.map(e => e.intelligence_engine).filter(Boolean)))
    : [];

  // Helper functions for engine display
  const getEngineLabel = (engine: string | undefined): string => {
    if (!engine) return 'Unknown';
    const labels: Record<string, string> = {
      'athletic_intelligence_orchestrator': 'Training',
      'nutrition_intelligence_engine': 'Nutrition',
      'sleep_intelligence_engine': 'Sleep',
      'movement_intelligence_engine': 'Movement',
      'equipment_intelligence_engine': 'Equipment',
      'womens_health_intelligence_engine': "Women's Health",
      'medical_intelligence_engine': 'Health',
      'physiotherapy_intelligence_engine': 'Physio'
    };
    return labels[engine] || engine;
  };

  const getEngineActiveClass = (engine: string) => {
    if (engine.includes('nutrition')) return 'bg-orange-100 text-orange-800';
    if (engine.includes('sleep')) return 'bg-purple-100 text-purple-800';
    if (engine.includes('movement')) return 'bg-blue-100 text-blue-800';
    if (engine.includes('womens_health')) return 'bg-pink-100 text-pink-800';
    if (engine.includes('medical')) return 'bg-red-100 text-red-800';
    if (engine.includes('physiotherapy')) return 'bg-teal-100 text-teal-800';
    return 'bg-green-100 text-green-800';
  };

  return (
    <div className="space-y-4">
      {/* Main Filter Buttons */}
      <div className="px-4">
        <div className="sr-chip-row" role="tablist" aria-label="Research event filters">
          {(['recent', 'move', 'chat'] as ResearchEventFilter[]).map((filterOption) => (
            <button
              key={filterOption}
              type="button"
              className={`sr-chip ${filter === filterOption ? 'active' : ''}`}
              onClick={() => handleFilterChange(filterOption)}
            >
              {filterOption === 'recent' && <Activity size={16} />}
              {filterOption === 'move' && <Zap size={16} />}
              {filterOption === 'chat' && (
                <ChatWithCedarIcon 
                  size="small" 
                  variant="nav"
                  active={filter === 'chat'}
                  className="inline-block"
                  style={{ width: '20px', height: '20px' }}
                />
              )}
              {filterOption === 'recent' && 'Recent'}
              {filterOption === 'move' && 'Plan'}
              {filterOption === 'chat' && 'Cedar'}
            </button>
          ))}
        </div>
      </div>

      {/* Subcategory Filters (for Chat filter only) */}
      {filter === 'chat' && (
        <div className="px-4">
          <div className="space-y-2">
            {/* Subcategory Chips */}
            <div className="flex items-center gap-2">
              <Filter size={14} style={{ color: 'var(--cedar-dark)' }} />
              <div className="sr-chip-row" aria-label="Chat subcategory filters">
                <button
                  type="button"
                  className={`sr-chip ${!subcategory ? 'active' : ''}`}
                  onClick={() => setSubcategory(undefined)}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`sr-chip ${subcategory === 'analysis' ? 'active' : ''}`}
                  onClick={() => setSubcategory('analysis')}
                >
                  Analysis
                </button>
                <button
                  type="button"
                  className={`sr-chip ${subcategory === 'question' ? 'active' : ''}`}
                  onClick={() => setSubcategory('question')}
                >
                  Questions
                </button>
              </div>
            </div>

            {/* Engine Tag Filters */}
            {availableEngines.length > 0 && (
              <div className="sr-chip-row" aria-label="Topic filters">
                <button
                  type="button"
                  className={`sr-chip ${!engine ? 'active' : ''}`}
                  onClick={() => setEngine(undefined)}
                >
                  All Topics
                </button>
                {availableEngines.map((eng) => (
                  <button
                    key={eng}
                    type="button"
                    className={`sr-chip ${engine === eng ? 'active' : ''}`}
                    onClick={() => setEngine(eng)}
                  >
                    {getEngineLabel(eng)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {data && (
        <div className="px-4">
          <div className="rounded-lg p-3" style={{ 
            backgroundColor: 'var(--cedar-subtle)',
            border: 'none' 
          }}>
            <p className="text-sm" style={{ color: 'var(--cedar-dark)' }}>
              <span className="font-semibold">{totalCount} events</span> in your research journal
              {filter === 'move' && ' from your plans'}
              {filter === 'chat' && ' from Cedar conversations'}
            </p>
          </div>
        </div>
      )}

      {/* 🔬 NEW: Return to Chat button when coming from attribution query */}
      {fromChat && highlightEventId && (
        <div className="px-4 mb-4">
          <button
            onClick={() => navigate('/chat')}
            className="w-full px-4 py-3 bg-white border-2 border-[var(--cedar-primary)] text-[var(--cedar-primary)] rounded-lg font-medium hover:bg-[var(--cedar-light)] transition-colors flex items-center justify-center gap-2"
          >
            <MessageCircle size={18} />
            Return to Chat
          </button>
        </div>
      )}

      {/* Events List */}
      <div className="px-4 space-y-3">
        {events.length > 0 ? (
          events.map((event) => (
            <ResearchEventCard
              key={event.id}
              event={event}
              sources={event.enriched_sources || []}
              primarySources={event.primary_sources || []}
              supportingSources={event.supporting_sources || []}
              enrichedFrameworks={event.enriched_frameworks || []}
              enrichedExperts={event.enriched_experts || []}
              onChatClick={handleChatWithCedar}
              initiallyExpanded={event.id === autoExpandedEventId}  // 🔬 NEW: Auto-expand target event
              eventElementId={`research-event-${event.id}`}  // 🔬 NEW: For scroll targeting
            />
          ))
        ) : (
          <EmptyState filter={filter} />
        )}
      </div>
    </div>
  );
};

// Empty State Component
interface EmptyStateProps {
  filter: ResearchEventFilter;
}

const EmptyState: React.FC<EmptyStateProps> = ({ filter }) => {
  return (
    <div className="text-center py-12">
      <svg className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--cedar-dark)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {filter === 'move' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        )}
        {filter === 'chat' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        )}
        {filter === 'recent' && (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        )}
      </svg>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {filter === 'recent' && "No research events yet"}
        {filter === 'move' && "No plan events yet"}
        {filter === 'chat' && "No Cedar conversations yet"}
      </h3>
      <p className="text-sm text-gray-600">
        {filter === 'recent' && "Research-backed decisions will appear here as you train"}
        {filter === 'move' && "Goal and plan creation events will appear here"}
        {filter === 'chat' && "Cedar intelligence conversations will appear here"}
      </p>
    </div>
  );
};
